import { useCustom, useTranslate } from '@refinedev/core';
import { Alert, Button, Progress, Space, Table, Tag, Typography } from 'antd';
import { format, parseISO } from 'date-fns';
import { enUS, ru } from 'date-fns/locale';
import { forwardRef, useImperativeHandle } from 'react';
import { Link } from 'react-router-dom';
import { PATHWAY_PROGRESS_FIELDS, type PathwayProgressRow } from './model';

/**
 * E34 · the client hub's pathways — the practice's enrolments of this client (and the courses the client
 * started while they share their record with the caller), each opening its "Client Hub — Pathway progress"
 * (cc-homework, chapter "Pathways — Define & enroll (portal, E34)"). Reads `clientPathways(subject)` — the
 * same PathwayProgress read, one row per enrolment. Sits on the Homework tab beside "Enroll in a pathway".
 */
export type ClientPathwaysHandle = { refetch: () => void };

type Props = { clientId: string; locale: 'en' | 'ru' };

export const ClientPathwaysSection = forwardRef<ClientPathwaysHandle, Props>(({ clientId, locale }, ref) => {
  const t = useTranslate();
  const { data, isFetching, isError, refetch } = useCustom<PathwayProgressRow[]>({
    url: '',
    method: 'get',
    errorNotification: false,
    queryOptions: { enabled: Boolean(clientId) },
    meta: {
      operation: 'clientPathways',
      fields: PATHWAY_PROGRESS_FIELDS,
      variables: { subject: { name: 'subject', type: 'ID!', value: clientId } },
    },
  });
  useImperativeHandle(ref, () => ({ refetch: () => void refetch() }), [refetch]);
  const rows = data?.data ?? [];
  const dateLocale = locale === 'ru' ? ru : enUS;
  const when = (iso: string | null | undefined) =>
    iso ? format(parseISO(iso), 'd MMM yyyy', { locale: dateLocale }) : '—';

  if (!rows.length && !isFetching && !isError) return null;

  return (
    <Space direction="vertical" size="small" style={{ width: '100%' }} data-testid="client-pathways">
      <Typography.Title level={5} style={{ margin: 0 }}>
        {t('pathways.hub.title')}
      </Typography.Title>
      {isError ? <Alert type="error" showIcon message={t('pathways.hub.loadFailed')} /> : null}
      <Table<PathwayProgressRow>
        rowKey="enrollmentId"
        size="small"
        loading={isFetching}
        dataSource={rows}
        pagination={false}
        onRow={(r) => ({ 'data-testid': `client-pathway-${r.enrollmentId}`, 'data-status': r.status } as never)}
        columns={[
          {
            title: t('pathways.hub.column.pathway'),
            render: (_, r) => (
              <Space direction="vertical" size={0}>
                <Typography.Text strong>{r.pathwayName}</Typography.Text>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {t('pathways.progress.meta', { version: r.pathwayVersion, date: when(r.startAt) })}
                  {r.selfEnrolled ? ` · ${t('pathways.progress.selfEnrolled')}` : ''}
                </Typography.Text>
              </Space>
            ),
          },
          {
            title: t('pathways.hub.column.status'),
            width: 130,
            render: (_, r) => (
              <Tag color={r.status === 'running' ? 'blue' : r.status === 'completed' ? 'green' : 'default'} data-testid={`client-pathway-status-${r.enrollmentId}`}>
                {t(`pathways.progress.status.${r.status}`)}
              </Tag>
            ),
          },
          {
            title: t('pathways.hub.column.progress'),
            width: 200,
            render: (_, r) => (
              <span data-testid={`client-pathway-percent-${r.enrollmentId}`}>
                <Progress percent={r.percentDone} size="small" />
              </span>
            ),
          },
          {
            title: '',
            width: 160,
            render: (_, r) => (
              <Link to={`/pathways/progress/${encodeURIComponent(r.enrollmentId)}`}>
                <Button size="small" data-testid={`client-pathway-open-${r.enrollmentId}`}>
                  {t('pathways.hub.open')}
                </Button>
              </Link>
            ),
          },
        ]}
      />
    </Space>
  );
});
ClientPathwaysSection.displayName = 'ClientPathwaysSection';
